/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { CommandBarButton } from '@fluentui/react/lib/Button';
import { ICommandBarItemProps } from '@fluentui/react/lib/CommandBar';
import * as React from 'react';
import { defineMessages } from 'react-intl';
import { LuisCommandBar } from 'src/core/components/LuisCommandBar';
import { RBACTooltip } from 'src/core/components/RBACTooltip';
import { TableCommandBarProps } from 'src/core/components/TableCommandBarProps';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { getCommandBarButtonStyles } from 'src/core/utils/azureRbacStyles';
import { mapUserRoleToUserActions } from 'src/core/utils/azureUtils';
import { commandBarItemSelectionManager } from 'src/core/utils/commandBarItemSelectionManager';
import { Sublist } from 'src/luis/projects/entities/types/Sublist';

export type NewSublistsTableCommandBarProps = TableCommandBarProps<Sublist> & {
    /**
     * A callback for when the import values
     * button is clicked.
     */
    onImportValues: () => boolean | void;

    /**
     * A callback for when a sublist is deleted.
     */
    onDeleteSublists: () => boolean | void;

    /**
     * User role to determine the actions.
     */
    userRole: string;

    /**
     * User role to show in LuisTooltip message.
     */
    displayedUserRole: string;
};

const messages = defineMessages({
    inputFieldPlaceholder: {
        id: 'newListEntityDetails.newSublistTableCommandBar.fieldPlaceholder',
        defaultMessage: 'Add new sublist ...'
    },
    searchFieldPlaceholder: {
        id: 'newListEntityDetails.newSublistTableCommandBar.searchFieldPlaceholder',
        defaultMessage: 'Search sublists ...'
    },
    deleteButton: {
        id: 'newListEntityDetails.newSublistTableCommandBar.deleteButton',
        defaultMessage: 'Delete'
    },
    importButton: {
        id: 'newListEntityDetails.newSublistTableCommandBar.importButton',
        defaultMessage: 'Import values'
    }
});

/**
 * Renders a command bar for the sublist table.
 *
 * @param props The input properties for the command bar.
 */
export const NewSublistsTableCommandBar = (props: NewSublistsTableCommandBarProps) => {
    const { selectedItems = [], query = '', onQueryChange, onDeleteSublists, onImportValues, userRole, displayedUserRole } = props;
    const { localize } = useLocalization();

    const { canWrite, canDeleteData } = mapUserRoleToUserActions(userRole);

    const commands: ICommandBarItemProps[] = [
        {
            key: 'import',
            onRender: (item: ICommandBarItemProps) => (
                <RBACTooltip
                    hidden={canWrite || item.disabled}
                    displayedUserRole={displayedUserRole}
                    data-automation-id="importButtonLuisToolTip"
                    componentSurroundedByToolTip={
                        <CommandBarButton
                            disabled={!canWrite || item.disabled}
                            onClick={onImportValues}
                            iconProps={{ iconName: 'Upload' }}
                            styles={getCommandBarButtonStyles(item.renderedInOverflow)}
                            data-automation-id="importButton"
                            name={localize(messages.importButton)}
                            text={localize(messages.importButton)}
                            ariaLabel={localize(messages.importButton)}
                        />
                    }
                />
            ),
            disabled: false
        },
        {
            key: 'delete',
            onRender: (item: ICommandBarItemProps) => (
                <RBACTooltip
                    hidden={canDeleteData || item.disabled}
                    displayedUserRole={displayedUserRole}
                    data-automation-id="deleteButtonLuisToolTip"
                    componentSurroundedByToolTip={
                        <CommandBarButton
                            disabled={!canDeleteData || item.disabled}
                            onClick={onDeleteSublists}
                            iconProps={{ iconName: 'Delete' }}
                            styles={getCommandBarButtonStyles(item.renderedInOverflow)}
                            data-automation-id="deleteButton"
                            name={localize(messages.deleteButton)}
                            text={localize(messages.deleteButton)}
                            ariaLabel={localize(messages.deleteButton)}
                        />
                    }
                />
            ),
            disabled: false
        }
    ];

    const items: ICommandBarItemProps[] = commandBarItemSelectionManager(
        commands,
        ['import'],
        ['delete'],
        ['delete'],
        selectedItems.length
    );

    return <LuisCommandBar items={items} includeSearchBar searchBarAlwaysExpanded onQueryChange={onQueryChange} query={query} />;
};
